import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import Input, { PasswordChecklist, PasswordInput } from '@orn/shared-web/components/Input';
import validatePassword from '@orn/shared-web/lib/validation/passwordValidation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import { OnboardingTitle } from '../../OnboardingLayout';

import type { IRegistrationDraft } from '../types';

interface IRegistrationStepProps {
  email: string;
  /**
   * Previously typed name fields, restored on mount when the user navigates
   * back to this step. Password fields are intentionally not persisted.
   */
  draft?: IRegistrationDraft;
  /**
   * If set, the user has already been created by an earlier submission of this
   * step (e.g. user navigated forward to OTP and came back). Submit skips the
   * register API and routes straight to OTP with this id — the server has no
   * "update registration" endpoint, so any inline edits to the name fields
   * are visual-only on resubmit.
   */
  existingUserId?: string;
  /**
   * Called whenever the user edits a persisted field so LoginFlow can stash
   * the latest draft in its state and re-hydrate on next mount.
   */
  onDraftChange: (draft: IRegistrationDraft) => void;
  onNext: (result: { userId: string }) => void;
  onBack: () => void;
}

interface IRegistrationFormValues {
  firstName: string;
  lastName: string;
  preferredName: string;
  password: string;
  confirmPassword: string;
}

/**
 * Registration step that collects the user's name and password for a new
 * account. Displays the email in a read-only input for context. Persists
 * name fields to `onDraftChange` on every keystroke so the parent can
 * re-hydrate the step on back-navigation. If `existingUserId` is set (the
 * user already registered but navigated back from OTP), submit skips the
 * API and routes forward directly.
 */
const RegistrationStep = ({ email, draft, existingUserId, onDraftChange, onNext, onBack }: IRegistrationStepProps) => {
  const authApi = useAuthApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [serverPasswordError, setServerPasswordError] = React.useState<string | undefined>();
  const form = useForm<IRegistrationFormValues>({
    defaultValues: {
      firstName: draft?.firstName ?? '',
      lastName: draft?.lastName ?? '',
      preferredName: draft?.preferredName ?? '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  // Bubble the persisted fields up whenever they change so LoginFlow can
  // re-hydrate this step on the next mount.
  React.useEffect(() => {
    const subscription = form.watch((values) => {
      onDraftChange({
        firstName: values.firstName ?? '',
        lastName: values.lastName ?? '',
        preferredName: values.preferredName ?? '',
      });
    });
    return () => subscription.unsubscribe();
  }, [form, onDraftChange]);

  const firstNameValue = form.watch('firstName');
  const lastNameValue = form.watch('lastName');
  const passwordValue = form.watch('password');
  const confirmValue = form.watch('confirmPassword');

  const handleSubmit = React.useCallback(
    async (values: IRegistrationFormValues) => {
      setServerPasswordError(undefined);

      // Client-side password checks
      const { rules } = validatePassword(values.password);
      if (!rules.every((r) => r.met)) {
        return;
      }

      if (values.password !== values.confirmPassword) {
        form.setError('confirmPassword', { message: 'Passwords do not match.' });
        return;
      }

      // User was already created on a previous submit and navigated back — skip
      // the API and route forward; the OTP screen still allows "Resend code"
      // if the original code expired while they were away.
      if (existingUserId) {
        onNext({ userId: existingUserId });
        return;
      }

      setIsSubmitting(true);
      const trimmedPreferredName = values.preferredName.trim();
      const result = await authApi.register({
        email,
        password: values.password,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        preferredName: trimmedPreferredName === '' ? undefined : trimmedPreferredName,
      });
      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === ApiErrorCode.AUTH_WEAK_PASSWORD) {
          setServerPasswordError(result.error.error);
        } else if (result.error.code === ApiErrorCode.AUTH_PROVIDER_CONFLICT) {
          form.setError('firstName', {
            message: 'This email uses Google sign-in. Please go back and use the Google button.',
          });
        } else if (result.error.code === ApiErrorCode.AUTH_EMAIL_ALREADY_REGISTERED) {
          form.setError('firstName', { message: 'This email is already registered. Please sign in instead.' });
        } else if (result.error.code === ApiErrorCode.AUTH_INVALID_FIRST_NAME) {
          form.setError('firstName', { message: result.error.error });
        } else if (result.error.code === ApiErrorCode.AUTH_INVALID_LAST_NAME) {
          form.setError('lastName', { message: result.error.error });
        } else if (result.error.code === ApiErrorCode.AUTH_INVALID_PREFERRED_NAME) {
          form.setError('preferredName', { message: result.error.error });
        } else {
          form.setError('firstName', { message: result.error.error });
        }
        return;
      }

      onNext({ userId: result.data.userId });
    },
    [authApi, email, existingUserId, form, onNext]
  );

  const passwordValidation = validatePassword(passwordValue);
  const passwordsMatch = passwordValue.length > 0 && confirmValue.length > 0 && passwordValue === confirmValue;
  const isPasswordValid = passwordValidation.rules.every((r) => r.met);
  const isFormFilled =
    firstNameValue.trim() !== '' && lastNameValue.trim() !== '' && passwordValue !== '' && confirmValue !== '';

  return (
    <>
      <OnboardingTitle title="Tell us about yourself" onBack={onBack} />

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-4">
          <Input label="Email" type="email" value={email} size="md" isDisabled />

          {/* Stack on mobile; side-by-side from the tablet breakpoint up */}
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-3">
            <FormField control={form.control} name="firstName">
              {({ field, fieldState }) => (
                <Input
                  {...field}
                  autoFocus
                  label="First name"
                  placeholder="John"
                  size="md"
                  isRequired
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="given-name"
                />
              )}
            </FormField>

            <FormField control={form.control} name="lastName">
              {({ field, fieldState }) => (
                <Input
                  {...field}
                  label="Last name"
                  placeholder="Doe"
                  size="md"
                  isRequired
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="family-name"
                />
              )}
            </FormField>
          </div>

          <FormField control={form.control} name="preferredName">
            {({ field, fieldState }) => (
              <Input
                {...field}
                label="Preferred name (optional)"
                placeholder="e.g. Johnny"
                size="md"
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
                autoComplete="nickname"
              />
            )}
          </FormField>

          <FormField control={form.control} name="password">
            {({ field }) => (
              <PasswordInput {...field} label="Password" size="md" isRequired autoComplete="new-password" />
            )}
          </FormField>

          <FormField control={form.control} name="confirmPassword">
            {({ field, fieldState }) => (
              <PasswordInput
                {...field}
                label="Confirm password"
                size="md"
                isRequired
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
                autoComplete="new-password"
              />
            )}
          </FormField>

          {/* Password checklist below both password fields */}
          <PasswordChecklist
            password={passwordValue}
            confirmPassword={confirmValue}
            serverError={serverPasswordError}
          />

          <Button
            type="submit"
            size="xl"
            color="quaternary"
            className="w-full"
            isDisabled={!isFormFilled || !isPasswordValid || !passwordsMatch}
            isLoading={isSubmitting}
          >
            Continue with email
          </Button>
        </div>
      </HookForm>
    </>
  );
};

RegistrationStep.displayName = 'RegistrationStep';

export default RegistrationStep;
